import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { Typography } from '@weave-mui/material';
import { fetchPlatformAnnouncement } from 'utils/api.service';
import { isPlatformAnnouncementVisible } from 'utils/platform/announcementVisibility';

/**
 * Platform-wide maintenance banner for authenticated Testing Hub users.
 * Loads config from CloudFront when REACT_APP_PLATFORM_ANNOUNCEMENT_URL is set.
 */
const PlatformAnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadAnnouncement = async () => {
      try {
        const data = await fetchPlatformAnnouncement();
        if (!cancelled) {
          setAnnouncement(data);
        }
      } catch {
        if (!cancelled) {
          setAnnouncement(null);
        }
      }
    };

    loadAnnouncement();

    const intervalId = window.setInterval(loadAnnouncement, 300000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  const visible =
    announcement !== null &&
    isPlatformAnnouncementVisible({ announcement, product: 'testinghub' });

  if (!visible) {
    return null;
  }

  const linkUrl = (announcement.linkUrl || '').trim();
  const linkLabel = (announcement.linkLabel || '').trim() || 'Learn More';
  const severity = announcement.severity || 'warning';

  return (
    <Box sx={{ width: '100%' }}>
      <Alert
        severity={severity}
        role={severity === 'info' ? 'status' : 'alert'}
        sx={{
          borderRadius: 0,
          py: 1.25,
          px: 2,
          alignItems: 'center',
          '& .MuiAlert-message': {
            width: '100%',
            py: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <Typography
            variant="body1"
            component="span"
            sx={{ flex: 1, minWidth: 0, fontSize: '1rem' }}
          >
            {announcement.message}
          </Typography>
          {linkUrl !== '' && (
            <Typography
              variant="body1"
              component="a"
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: 'inherit',
              }}
            >
              {linkLabel}
            </Typography>
          )}
        </Box>
      </Alert>
    </Box>
  );
};

export default PlatformAnnouncementBanner;
